import { auth } from "@/lib/firebase";
import { signInWithCustomToken, onAuthStateChanged } from "firebase/auth";

/**
 * Waits for Firebase auth to be ready and returns the current user
 */
async function waitForAuth(timeout = 5000): Promise<boolean> {
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth!, (user) => {
      if (user) {
        console.log("waitForAuth: User detected:", user.uid);
        unsubscribe();
        resolve(true);
      }
    });

    // Timeout after specified time
    setTimeout(() => {
      unsubscribe();
      console.log("waitForAuth: Timeout reached, no user detected");
      resolve(false);
    }, timeout);
  });
}

/**
 * Ensures the Firebase client SDK is authenticated by using a custom token
 * from the server session. This syncs server-side session auth with client-side
 * Firebase Auth, which is required for RTDB access.
 */
export async function ensureClientAuth(): Promise<boolean> {
  try {
    console.log("ensureClientAuth: Starting...");

    // Check if auth is initialized
    if (!auth) {
      console.error("ensureClientAuth: Firebase auth not initialized");
      return false;
    }

    // If we already have a current user, just ensure the session is valid
    if (auth.currentUser) {
      console.log(
        "ensureClientAuth: Already authenticated as:",
        auth.currentUser.uid
      );

      // Force refresh the token to ensure it's valid
      try {
        const token = await auth.currentUser.getIdToken(true);
        console.log("ensureClientAuth: Token refreshed successfully");
        
        // Check if server has a valid session - use a short timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);
        
        try {
          const sessionCheckResponse = await fetch("/api/auth/verify-session", {
            credentials: "include",
            signal: controller.signal,
          });
          clearTimeout(timeoutId);
          
          if (!sessionCheckResponse.ok) {
            console.log("ensureClientAuth: No valid server session, creating one...");
            
            // Create server-side session cookie
            const sessionResponse = await fetch("/api/auth/session", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ idToken: token }),
              credentials: "include",
            });
            
            if (sessionResponse.ok) {
              console.log("ensureClientAuth: Server session created successfully");
            } else {
              console.error("ensureClientAuth: Failed to create server session");
            }
          } else {
            console.log("ensureClientAuth: Server session is valid");
          }
        } catch (fetchError: any) {
          clearTimeout(timeoutId);
          if (fetchError.name === 'AbortError') {
            console.warn("ensureClientAuth: Session check timed out, continuing anyway");
          } else {
            console.warn("ensureClientAuth: Session check failed:", fetchError.message);
          }
        }
      } catch (refreshError) {
        console.error("ensureClientAuth: Token refresh failed:", refreshError);
      }

      return true;
    }

    // Wait briefly to see if auth state is being restored
    const hasExistingAuth = await waitForAuth(500);
    
    if (hasExistingAuth && auth.currentUser) {
      console.log("ensureClientAuth: Auth state restored for:", (auth.currentUser as any).uid);
      return true;
    }

    console.log("ensureClientAuth: No current user, fetching custom token...");

    // Get a custom token from the server using the session cookie
    const response = await fetch("/api/auth/custom-token", {
      credentials: "include",
    });

    console.log(
      "ensureClientAuth: Custom token response status:",
      response.status
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      console.error("ensureClientAuth: Failed to get custom token:", {
        status: response.status,
        error: errorData,
      });
      return false;
    }

    const { customToken } = await response.json();

    if (!customToken) {
      console.error("ensureClientAuth: No custom token in response");
      return false;
    }

    console.log("ensureClientAuth: Got custom token, signing in...");

    // Sign in with the custom token (don't sign out first - it causes issues)
    const userCredential = await signInWithCustomToken(auth, customToken);
    console.log(
      "ensureClientAuth: Successfully authenticated as:",
      userCredential.user.uid
    );

    // Short delay for auth state to propagate
    await new Promise((resolve) => setTimeout(resolve, 500));

    console.log("ensureClientAuth: Auth sync complete");
    return true;
  } catch (error) {
    console.error("ensureClientAuth: Error:", error);

    // Log more details if it's a Firebase error
    if (error && typeof error === "object" && "code" in error) {
      console.error(
        "ensureClientAuth: Firebase error code:",
        (error as any).code
      );
      console.error(
        "ensureClientAuth: Firebase error message:",
        (error as any).message
      );
    }

    return false;
  }
}

/**
 * Debug function to test RTDB access with current auth state
 */
export async function debugRTDBAccess(userId: string) {
  console.log("=== RTDB Debug Test ===");

  try {
    const { rtdb, auth } = await import("@/lib/firebase");
    const { ref, get, set } = await import("firebase/database");

    if (!rtdb || !auth) {
      console.error("RTDB Debug: Firebase not initialized");
      return;
    }

    console.log("RTDB Debug: Current auth state:", {
      currentUser: auth.currentUser?.uid,
      isAuthenticated: !!auth.currentUser,
    });

    // Test 0: Try the simplest auth-only path
    console.log("RTDB Debug: Testing read at test-auth");
    try {
      const testAuthRef = ref(rtdb, "test-auth");
      const testSnapshot = await get(testAuthRef);
      console.log("RTDB Debug: test-auth read successful!", {
        exists: testSnapshot.exists(),
        value: testSnapshot.val(),
      });
    } catch (testAuthError) {
      console.error("RTDB Debug: test-auth read failed:", testAuthError);
    }

    // Test 1: Try to read the quickMatches path
    console.log(`RTDB Debug: Testing read at quickMatches/${userId}`);
    try {
      const readRef = ref(rtdb, `quickMatches/${userId}`);
      const snapshot = await get(readRef);
      console.log("RTDB Debug: Read successful!", {
        exists: snapshot.exists(),
        value: snapshot.val(),
      });
    } catch (readError) {
      console.error("RTDB Debug: Read failed:", readError);
    }

    // Test 2: Try to write to the quickMatches path
    console.log(`RTDB Debug: Testing write at quickMatches/${userId}/test`);
    try {
      const writeRef = ref(rtdb, `quickMatches/${userId}/test`);
      await set(writeRef, { timestamp: Date.now() });
      console.log("RTDB Debug: Write successful!");

      // Clean up test data
      await set(writeRef, null);
    } catch (writeError) {
      console.error("RTDB Debug: Write failed:", writeError);
    }

    // Test 3: Try a different path that should be accessible
    console.log("RTDB Debug: Testing read at root level");
    try {
      const rootRef = ref(rtdb);
      await get(rootRef);
      console.log("RTDB Debug: Root read attempted (this should fail)");
    } catch (rootError: any) {
      console.log(
        "RTDB Debug: Root read failed as expected:",
        rootError.code || rootError.message
      );
    }

    console.log("=== RTDB Debug Test Complete ===");
  } catch (error) {
    console.error("RTDB Debug: Unexpected error:", error);
  }
}
